import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  Heading,
  Input,
  InputGroup,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useFetchUser } from "../hooks/useFetchUser";
import { useUpdateUser } from "../hooks/useUpdateUser";
import { toaster } from "./ui/toaster";

type Props = {
  open: boolean;
  onClose: () => void;
};

const ProfileModal = ({ open, onClose }: Props): JSX.Element => {
  const { user, refetch } = useFetchUser();
  const [formData, setFormData] = useState<{
    displayName: string;
    phoneNumber: string;
  }>({
    displayName: "",
    phoneNumber: "",
  });

  useEffect(() => {
    if (user) {
      setFormData({
        displayName: user.displayName || "",
        phoneNumber: user.phoneNumber || "",
      });
    }
  }, [user, open]);

  const { updateUser, isLoading } = useUpdateUser(
    () => {
      toaster.create({
        title: "Usuario actualizado",
        description: "La información se ha actualizado con éxito.",
        type: "success",
        duration: 5000,
        meta: { closable: true },
      });
    },
    () => {
      toaster.create({
        title: "Error",
        description: "Hubo un error al actualizar la información.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  );

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/\D/g, "");
    value = value.slice(0, 10);
    setFormData({ ...formData, phoneNumber: value });
  };

  const isDataDifferent = () => {
    const userDisplayName = user?.displayName || "";
    const userPhoneNumber = user?.phoneNumber || "";

    return (
      formData.displayName !== userDisplayName ||
      formData.phoneNumber !== userPhoneNumber
    );
  };

  const isFormValid = () => {
    return (
      formData.phoneNumber === "" ||
      /^3[0-9]{9}$/.test(formData.phoneNumber.replace(/^57/, ""))
    );
  };

  const handleSubmit = async () => {
    await updateUser(formData);
    refetch();
    onClose();
  };

  const resetForm = () => {
    setFormData({
      displayName: "",
      phoneNumber: "",
    });
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
          resetForm();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="md" maxWidth="400px">
            <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
              <Dialog.Title>
                <Heading>Perfil</Heading>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton marginTop="1" onClick={onClose} />
            </Dialog.CloseTrigger>
            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                <HStack justify="center">
                  <Text fontWeight={600} maxWidth="100%">
                    {user?.email}
                  </Text>
                </HStack>

                <Box>
                  <Text fontWeight={600} mb={2}>
                    Nombre
                  </Text>
                  <Input
                    value={formData?.displayName}
                    onChange={(e) =>
                      setFormData({ ...formData, displayName: e.target.value })
                    }
                    type="text"
                    placeholder="Juan Pérez"
                  />
                </Box>

                <Box>
                  <Field.Root required>
                    <Field.Label>Teléfono</Field.Label>
                    <InputGroup startAddon="+57">
                      <Input
                        value={formData.phoneNumber}
                        onChange={handlePhoneNumberChange}
                        maxLength={10}
                        placeholder="000 000 00 00"
                      />
                    </InputGroup>
                  </Field.Root>
                </Box>

                <Box display={"flex"} gap={"1rem"} flexDirection={"row"}>
                  <Button
                    loading={isLoading}
                    onClick={async () => await handleSubmit()}
                    disabled={!isDataDifferent() || !isFormValid()}
                    flexGrow={1}
                  >
                    Guardar
                  </Button>
                </Box>
              </VStack>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ProfileModal;
